import React from "react"
import { Badge, Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap"
import ImageUrlField from "components/Shared/ImageUrlField"
import {
  ColorField,
  TextField,
} from "modules/website-builder/pages/WebsiteBuilderFields"
import { QR_TEXT_FIELDS } from "modules/website-builder/constants/qrWebsiteConfigBootstrap"
import { getDeepValue, setDeepValue, setQrTextLang } from "modules/website-builder/constants/qrFormUtils"

function QrImageField({ form, path, label, t, onChange, purpose, md = 6 }) {
  return (
    <ImageUrlField
      label={label}
      value={getDeepValue(form, path) || ""}
      onChange={url => onChange(prev => setDeepValue(prev, path, url))}
      t={t}
      md={md}
      purpose={purpose}
    />
  )
}

export function QrTemplatesTab({ templates, form, t }) {
  const active =
    templates?.find(template => template.id === form.templateId) ||
    templates?.[0] ||
    null

  if (!active) {
    return (
      <p className="text-muted mb-0">
        {t("No QR website templates are available yet.")}
      </p>
    )
  }

  return (
    <Row>
      <Col lg="8">
        <Card className="border border-primary">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
              <h5 className="mb-0">{active.name}</h5>
              <Badge color="success">{t("Active")}</Badge>
            </div>
            <p className="text-muted">{active.description}</p>
            {Array.isArray(active.highlights) && active.highlights.length ? (
              <ul className="mb-0 ps-3">
                {active.highlights.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export function QrBrandTab({
  form,
  t,
  onChange,
  savedThemeBaseline,
  savedThemeBaselineAt,
  savingThemeBaseline,
  restoringThemeBaseline,
  onSaveThemeBaseline,
  onRestoreThemeBaseline,
  readOnly = false,
}) {
  return (
    <>
      <Row>
        <Col md="12" className="mb-3">
          <Card className="border bg-light">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div>
                  <h5 className="mb-1">{t("My Saved Design")}</h5>
                  <p className="text-muted small mb-0">
                    {t(
                      "Save your full QR website setup — brand, colors, assets, and text. Stored separately and only updates when you click Save My Design."
                    )}
                  </p>
                  {savedThemeBaselineAt ? (
                    <p className="text-muted small mb-0 mt-2">
                      {t("Last saved at {{date}}", {
                        date: new Date(savedThemeBaselineAt).toLocaleString(),
                      })}
                    </p>
                  ) : (
                    <p className="text-muted small mb-0 mt-2">
                      {t(
                        "No saved design yet. Configure your QR website, then save your snapshot."
                      )}
                    </p>
                  )}
                  <p className="text-muted small mb-0 mt-2">
                    {t(
                      "If you published before experimenting, you can also use Discard Draft in the header to revert all draft changes."
                    )}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    color="primary"
                    outline
                    onClick={onRestoreThemeBaseline}
                    disabled={
                      readOnly || !savedThemeBaseline || restoringThemeBaseline
                    }
                  >
                    {restoringThemeBaseline
                      ? t("Restoring...")
                      : t("Reset to Saved Design")}
                  </Button>
                  <Button
                    color="primary"
                    onClick={onSaveThemeBaseline}
                    disabled={readOnly || savingThemeBaseline}
                  >
                    {savingThemeBaseline ? t("Saving...") : t("Save My Design")}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <TextField form={form} path="name" label="Brand name" t={t} onChange={onChange} md={6} />
        <TextField form={form} path="title" label="Site title" t={t} onChange={onChange} md={6} />
      </Row>
      <Row>
        <ColorField
          form={form}
          path="colors.primary"
          label="Primary color"
          t={t}
          onChange={onChange}
          fallback="#E57A98"
        />
        <ColorField
          form={form}
          path="colors.secondary"
          label="Secondary color"
          t={t}
          onChange={onChange}
          fallback="#1A1A1A"
        />
      </Row>
    </>
  )
}

export function QrAssetsTab({ form, t, onChange }) {
  return (
    <Row>
      <QrImageField
        form={form}
        path="assets.logo"
        label="Logo"
        t={t}
        onChange={onChange}
        purpose="qr-logo"
      />
      <QrImageField
        form={form}
        path="assets.logoDark"
        label="Logo (dark)"
        t={t}
        onChange={onChange}
        purpose="qr-logo-dark"
      />
      <QrImageField
        form={form}
        path="assets.favicon"
        label="Favicon"
        t={t}
        onChange={onChange}
        purpose="qr-favicon"
      />
      <QrImageField
        form={form}
        path="assets.loginBackground"
        label="Login background"
        t={t}
        onChange={onChange}
        purpose="qr-login-background"
      />
    </Row>
  )
}

export function QrTextTab({ form, t, onChange }) {
  return (
    <>
      {QR_TEXT_FIELDS.map(field => {
        const entry = form?.text?.[field.key] || {}
        return (
          <Card key={field.key} className="border mb-3">
            <CardBody>
              <h6 className="mb-1">{t(field.label)}</h6>
              <p className="text-muted small mb-3">{t(field.section)}</p>
              <Row>
                <Col md="6">
                  <Label>{t("English")}</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={entry.en || ""}
                    onChange={event =>
                      onChange(prev => setQrTextLang(prev, field.key, "en", event.target.value))
                    }
                  />
                </Col>
                <Col md="6">
                  <Label>{t("Arabic")}</Label>
                  <Input
                    type="textarea"
                    rows={2}
                    value={entry.ar || ""}
                    onChange={event =>
                      onChange(prev => setQrTextLang(prev, field.key, "ar", event.target.value))
                    }
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        )
      })}
    </>
  )
}

export function QrPublishTab({
  form,
  t,
  publishedAt,
  publishedSnapshot,
  hasUnpublishedChanges,
}) {
  const previewColors = form?.colors || {}
  const previewAssets = form?.assets || {}

  return (
    <>
      <Row className="mb-3">
        <Col md="6">
          <Card className="border h-100">
            <CardBody>
              <h6 className="mb-3">{t("Draft preview")}</h6>
              <p className="mb-1">
                <strong>{form.name}</strong> — {form.title}
              </p>
              <div className="d-flex gap-2 mb-3">
                <span
                  className="rounded"
                  style={{
                    width: 28,
                    height: 28,
                    backgroundColor: previewColors.primary,
                    border: "1px solid #dee2e6",
                  }}
                  title={previewColors.primary}
                />
                <span
                  className="rounded"
                  style={{
                    width: 28,
                    height: 28,
                    backgroundColor: previewColors.secondary,
                    border: "1px solid #dee2e6",
                  }}
                  title={previewColors.secondary}
                />
              </div>
              <ul className="small text-muted mb-0 ps-3">
                <li>{t("Logo")}: {previewAssets.logo || "—"}</li>
                <li>{t("Favicon")}: {previewAssets.favicon || "—"}</li>
              </ul>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card className="border h-100">
            <CardBody>
              <h6 className="mb-3">{t("Publish status")}</h6>
              {hasUnpublishedChanges ? (
                <Badge color="warning" className="mb-2">
                  {t("Unpublished changes")}
                </Badge>
              ) : (
                <Badge color="success" className="mb-2">
                  {t("Published")}
                </Badge>
              )}
              {publishedAt ? (
                <p className="text-muted small mb-0">
                  {t("Last published")}: {new Date(publishedAt).toLocaleString()}
                </p>
              ) : (
                <p className="text-muted small mb-0">
                  {t("This QR website has not been published yet.")}
                </p>
              )}
              {publishedSnapshot ? (
                <p className="text-muted small mt-2 mb-0">
                  {t("Live version")}: {publishedSnapshot.name} — {publishedSnapshot.title}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <p className="text-muted small mb-0">
        {t(
          "After publishing, the QR ordering app loads config from GET /api/v1/public/qr-website-config/:tenantId"
        )}
      </p>
    </>
  )
}
